// Chat log and chat field (online): player chat and server notices. Lines fade
// out after a while; the open field shows the whole history.

import { el } from './dom';

/** Chat lines kept in the log; the closed log shows only the newest few. */
const CHAT_HISTORY = 40;
const CHAT_FADE_MS = 12000;

export class ChatLog {
  readonly root = el('div', 'hud-chat');
  private readonly log = el('div', 'hud-chat-log');
  private readonly input = el('input', 'hud-chat-input');
  private done: ((text: string | null) => void) | null = null;

  constructor() {
    this.input.maxLength = 200;
    this.input.placeholder = 'Say… (/help for commands)';
    this.input.setAttribute('aria-label', 'Chat message');
    this.root.append(this.log, this.input);
    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') this.close(this.input.value.trim() || null);
      else if (e.key === 'Escape') this.close(null);
    });
    this.input.addEventListener('blur', () => this.close(null));
  }

  /** Adds a line (from null = server notice). */
  add(from: string | null, text: string): void {
    const line = el('div', `hud-chat-line${from === null ? ' info' : ''}`);
    if (from !== null) line.appendChild(el('span', 'from', `${from}: `));
    line.appendChild(document.createTextNode(text));
    this.log.appendChild(line);
    while (this.log.children.length > CHAT_HISTORY) this.log.firstChild?.remove();
    this.log.scrollTop = this.log.scrollHeight;
    setTimeout(() => line.classList.add('fade'), CHAT_FADE_MS);
  }

  get open(): boolean {
    return this.done !== null;
  }

  /** Opens the field; `done` gets the text (null when cancelled). */
  openField(done: (text: string | null) => void): void {
    if (this.done) return;
    this.done = done;
    this.input.value = '';
    this.root.classList.add('open');
    this.input.focus();
  }

  close(text: string | null = null): void {
    const done = this.done;
    if (!done) return;
    this.done = null;
    this.root.classList.remove('open');
    this.input.blur();
    done(text);
  }

  clear(): void {
    this.close(null);
    this.log.replaceChildren();
  }
}
